import { Badge, Box, Flex, Heading, Spinner, Text } from '@chakra-ui/react'
import { ChildCheckinRecord } from '@/modules/teacher/hooks/useChildCheckinHistory'

type Props = {
    history: ChildCheckinRecord[]
    loading?: boolean
    /** Tighter type scale and no heading, for use inside a dense card. */
    compact?: boolean
    /** Off when the caller supplies its own header (e.g. a collapse toggle). */
    showTitle?: boolean
    title?: string
    emptyMessage?: string
}

const formatDate = (value?: string | null) => {
    if (!value) return null
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return null
    return date.toLocaleDateString(undefined, {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    })
}

const formatTime = (value?: string | null) => {
    if (!value) return null
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return null
    return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
}

/** Recent check-in/check-out pairs for one child. */
export default function CheckinHistoryList({
    history,
    loading,
    compact = false,
    showTitle = true,
    title = 'Recent history',
    emptyMessage = 'No previous check-ins for this child.'
}: Props) {
    return (
        <Box mt={compact ? 0 : 6}>
            <Flex align="center" gap={2} mb={2} display={showTitle ? 'flex' : 'none'}>
                {compact ? (
                    <Text
                        fontSize="xs"
                        textTransform="uppercase"
                        color="gray.500"
                        letterSpacing="wide"
                    >
                        {title}
                    </Text>
                ) : (
                    <Heading fontSize="md">{title}</Heading>
                )}
                {loading ? <Spinner size="xs" color="gray.400" /> : null}
            </Flex>

            {history.length === 0 && !loading ? (
                <Text fontSize={compact ? 'xs' : 'sm'} color="gray.500">
                    {emptyMessage}
                </Text>
            ) : (
                <Box
                    borderWidth={compact ? 0 : '1px'}
                    borderColor="gray.100"
                    borderRadius="md"
                >
                    {history.map((record, index) => {
                        const inTime = formatTime(record.checkedInDate)
                        const outTime = formatTime(record.checkedOutDate)
                        return (
                            <Flex
                                key={record._id}
                                align="center"
                                gap={3}
                                px={compact ? 0 : 3}
                                py={compact ? 1 : 2}
                                borderTopWidth={index === 0 || compact ? 0 : '1px'}
                                borderColor="gray.50"
                            >
                                <Box minW={0} flex="1">
                                    <Text
                                        fontSize={compact ? 'xs' : 'sm'}
                                        fontWeight="medium"
                                        noOfLines={1}
                                    >
                                        {formatDate(record.checkedInDate) ?? '—'}
                                        {record.session ? ` · ${record.session}` : ''}
                                    </Text>
                                    <Text fontSize="xs" color="gray.500" noOfLines={1}>
                                        in {inTime ?? '—'} ·{' '}
                                        {record.checkedOut
                                            ? `out ${outTime ?? '—'}`
                                            : 'not checked out'}
                                        {record.checkinCode
                                            ? ` · ${record.checkinCode}`
                                            : ' · no code'}
                                    </Text>
                                </Box>
                                <Badge
                                    flexShrink={0}
                                    fontSize={compact ? '10px' : undefined}
                                    colorScheme={record.checkedOut ? 'gray' : 'orange'}
                                >
                                    {record.checkedOut ? 'Closed' : 'Open'}
                                </Badge>
                            </Flex>
                        )
                    })}
                </Box>
            )}
        </Box>
    )
}
